import { Link } from "react-router-dom";
import {
  UserCheck,
  UserPlus,
  ScanFace,
  CheckCircle2,
  Car,
  Activity,
  ShieldCheck,
  Handshake,
  AlertTriangle,
} from "lucide-react";
import useSeo from "../hooks/useSeo";

const SECTIONS = [
  {
    id: "elegibilidad",
    n: "1",
    icon: UserCheck,
    title: "Elegibilidad",
    body: (
      <>
        <p>
          Los Servicios de RivoNes (<strong>“RivoNes”</strong>, <strong>“nosotros”</strong> o{" "}
          <strong>“la plataforma”</strong>) están disponibles únicamente para:
        </p>
        <h4>Usuarios (Arrendatarios):</h4>
        <ul>
          <li>Personas mayores de 21 años de edad</li>
          <li>Con licencia de conducir vigente y válida en México o licencia internacional válida</li>
          <li>Que cumplan con las leyes aplicables en México</li>
          <li>Que cuenten con un método de pago válido</li>
        </ul>
        <h4>Propietarios (Anfitriones):</h4>
        <ul>
          <li>Personas físicas o morales mayores de 21 años</li>
          <li>Con propiedad legal del vehículo o autorización para arrendarlo</li>
          <li>Vehículos registrados legalmente en México</li>
        </ul>
        <div className="mt-5 p-5 rounded-2xl border border-amber-200 bg-amber-50 not-prose">
          <p className="text-sm text-amber-900 font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Importante
          </p>
          <p className="text-sm text-amber-900 mt-1">
            El uso de la plataforma por personas que no cumplan estos requisitos está estrictamente
            prohibido.
          </p>
        </div>
      </>
    ),
  },
  {
    id: "registro",
    n: "2",
    icon: UserPlus,
    title: "Registro de Cuenta",
    body: (
      <>
        <p>
          Para utilizar ciertas funciones de la plataforma, debes crear una cuenta (
          <strong>“Cuenta RivoNes”</strong>).
        </p>
        <p>Durante el registro se solicitará:</p>
        <ul>
          <li>Nombre completo</li>
          <li>Correo electrónico</li>
          <li>Número de teléfono</li>
          <li>Contraseña segura</li>
          <li>Verificación vía SMS o correo electrónico</li>
        </ul>
        <p>
          También podrás registrarte mediante servicios de terceros (ej. Google o Apple, cuando esté
          disponible).
        </p>

        <h4>Información adicional requerida</h4>
        <p>Dependiendo del uso de la plataforma:</p>

        <h4>Para arrendatarios:</h4>
        <ul>
          <li>Licencia de conducir</li>
          <li>Identificación oficial (INE o pasaporte)</li>
          <li>Método de pago válido</li>
          <li>Depósito de garantía (según el vehículo)</li>
        </ul>

        <h4>Para anfitriones:</h4>
        <ul>
          <li>Documentación del vehículo:</li>
          <li className="ml-4">— Tarjeta de circulación</li>
          <li className="ml-4">— Seguro vigente</li>
          <li className="ml-4">— Fotografías del vehículo</li>
          <li>Información bancaria para recibir pagos</li>
        </ul>

        <p>El usuario se compromete a proporcionar información:</p>
        <ul>
          <li>Verdadera</li>
          <li>Actualizada</li>
          <li>Completa</li>
        </ul>
        <p>Y mantenerla actualizada en todo momento.</p>

        <p>RivoNes podrá requerir adicionalmente:</p>
        <ul>
          <li>Depósitos de seguridad</li>
          <li>Métodos de pago adicionales</li>
          <li>Selección obligatoria de protección o seguro</li>
          <li>Verificaciones antes de confirmar una reserva</li>
        </ul>
      </>
    ),
  },
  {
    id: "verificacion",
    n: "3",
    icon: ScanFace,
    title: "Verificación",
    body: (
      <>
        <p>
          RivoNes se reserva el derecho (sin obligación) de realizar procesos de verificación,
          incluyendo:
        </p>
        <h4>Verificación de usuarios:</h4>
        <ul>
          <li>Validación de identidad</li>
          <li>Validación de licencia de conducir</li>
          <li>Historial de conducción (cuando sea posible)</li>
          <li>Detección de fraude</li>
        </ul>
        <h4>Verificación de vehículos:</h4>
        <ul>
          <li>Documentación legal</li>
          <li>Condiciones del vehículo</li>
          <li>Cumplimiento de estándares mínimos de seguridad</li>
        </ul>

        <h4>Uso de terceros</h4>
        <p>RivoNes podrá utilizar servicios externos para:</p>
        <ul>
          <li>Verificar identidad</li>
          <li>Validar documentos</li>
          <li>Prevenir fraude</li>
          <li>Evaluar riesgo</li>
        </ul>
        <p>El usuario autoriza expresamente a RivoNes a:</p>
        <ul>
          <li>Solicitar</li>
          <li>Obtener</li>
          <li>Almacenar</li>
          <li>Utilizar</li>
        </ul>
        <p>esta información conforme a la legislación aplicable en México.</p>
      </>
    ),
  },
  {
    id: "aprobacion",
    n: "4",
    icon: CheckCircle2,
    title: "Aprobación o Rechazo",
    body: (
      <>
        <p>RivoNes podrá, a su entera discreción:</p>
        <ul>
          <li>Aprobar o rechazar registros</li>
          <li>Aprobar o rechazar reservas</li>
          <li>Suspender o cancelar cuentas</li>
        </ul>
        <p>
          Sin obligación de justificar la decisión, especialmente en casos de:
        </p>
        <ul>
          <li>Riesgo operativo</li>
          <li>Información incorrecta</li>
          <li>Incumplimiento de políticas</li>
        </ul>
      </>
    ),
  },
  {
    id: "responsabilidad-vehiculos",
    n: "5",
    icon: Car,
    title: "Responsabilidad sobre los Vehículos",
    body: (
      <>
        <p>Los anfitriones son totalmente responsables de que sus vehículos:</p>
        <ul>
          <li>Estén en condiciones seguras y operables</li>
          <li>Cumplan con las leyes de tránsito mexicanas</li>
          <li>Cuenten con seguro vigente</li>
          <li>No tengan reporte de robo</li>
          <li>No tengan estatus legal irregular</li>
          <li>No presenten fallas mecánicas peligrosas</li>
        </ul>
        <div className="mt-5 p-5 rounded-2xl border border-amber-200 bg-amber-50 not-prose">
          <p className="text-sm text-amber-900 font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            RivoNes no garantiza ni certifica:
          </p>
          <ul className="text-sm text-amber-900 mt-2 list-disc pl-5 space-y-1">
            <li>El estado mecánico del vehículo</li>
            <li>La seguridad del vehículo</li>
            <li>La legalidad absoluta del mismo</li>
          </ul>
        </div>
      </>
    ),
  },
  {
    id: "evaluacion-riesgo",
    n: "6",
    icon: Activity,
    title: "Autorización para Evaluación de Riesgo",
    body: (
      <>
        <p>Al utilizar la plataforma, el usuario autoriza a RivoNes a:</p>
        <ul>
          <li>Evaluar su perfil de riesgo</li>
          <li>Verificar información personal y financiera</li>
          <li>Analizar comportamiento dentro de la plataforma</li>
        </ul>
        <p>Todo conforme a:</p>
        <ul>
          <li>
            Ley Federal de Protección de Datos Personales en Posesión de los Particulares (México)
          </li>
        </ul>
        <div className="mt-5 p-5 rounded-2xl border border-blue-200 bg-blue-50 not-prose">
          <p className="text-sm text-blue-900 font-semibold">Nota importante</p>
          <p className="text-sm text-blue-900 mt-1">
            RivoNes no consultará buró de crédito sin consentimiento adicional explícito.
          </p>
        </div>
      </>
    ),
  },
  {
    id: "limitacion",
    n: "7",
    icon: ShieldCheck,
    title: "Seguridad y Limitación de Responsabilidad",
    body: (
      <>
        <p>
          RivoNes actúa únicamente como <strong>intermediario digital</strong> entre usuarios y
          propietarios.
        </p>
        <p>RivoNes no es responsable por:</p>
        <ul>
          <li>Conducta de los usuarios</li>
          <li>Estado real de los vehículos</li>
          <li>Incidentes durante el uso del servicio</li>
        </ul>
        <p>Sin embargo, se implementan medidas razonables para:</p>
        <ul>
          <li>Reducir riesgos</li>
          <li>Mejorar la seguridad</li>
          <li>Detectar actividades sospechosas</li>
        </ul>
      </>
    ),
  },
  {
    id: "aceptacion",
    n: "8",
    icon: Handshake,
    title: "Aceptación",
    body: (
      <>
        <p>Al registrarte o utilizar RivoNes, aceptas:</p>
        <ul className="list-none pl-0">
          <li className="flex gap-2">
            <span className="text-emerald-600 font-bold">✔</span> Cumplir con estos términos
          </li>
          <li className="flex gap-2">
            <span className="text-emerald-600 font-bold">✔</span> Proporcionar información verídica
          </li>
          <li className="flex gap-2">
            <span className="text-emerald-600 font-bold">✔</span> Someterte a procesos de
            verificación
          </li>
          <li className="flex gap-2">
            <span className="text-emerald-600 font-bold">✔</span> Asumir responsabilidad por el uso
            del servicio
          </li>
        </ul>
      </>
    ),
  },
];

export default function ElegibilidadPage() {
  useSeo({
    title: "Elegibilidad, Registro y Verificación · Rivones",
    description:
      "Requisitos de elegibilidad, registro de cuenta y procesos de verificación de usuarios y anfitriones en la plataforma RivoNes.",
    keywords:
      "elegibilidad rivones, registro rivones, verificación rivones, requisitos renta de autos México",
    canonical: "https://rivones.com/elegibilidad",
  });

  return (
    <div className="bg-sand min-h-screen" data-testid="elegibilidad-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-neutral-50 to-sand border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <p className="text-sm font-semibold tracking-[0.2em] text-terracotta uppercase">
            Documento legal
          </p>
          <h1
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[#111111] tracking-tight mt-3"
            data-testid="elegibilidad-h1"
          >
            Elegibilidad, Registro y Verificación
          </h1>
          <p className="text-base sm:text-lg text-neutral-600 mt-4 leading-relaxed">
            RivoNes — Desarrollado por <strong>Novus Core Technologies LLC</strong>.
          </p>
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 mt-6 font-semibold">
            Última actualización · Febrero 2026
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* TOC */}
        <nav
          aria-label="Índice"
          className="bg-white border border-neutral-200 rounded-2xl p-5 mb-10"
          data-testid="elegibilidad-toc"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">
            Índice
          </p>
          <ol className="space-y-1.5 text-sm">
            {SECTIONS.map(({ id, n, title }) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className="text-neutral-700 hover:text-terracotta transition-colors flex gap-2"
                >
                  <span className="text-neutral-400 tabular-nums">{n}.</span>
                  <span>{title}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-12">
          {SECTIONS.map(({ id, n, icon: Icon, title, body }) => (
            <article
              key={id}
              id={id}
              data-testid={`elegibilidad-section-${id}`}
              className="scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex w-11 h-11 rounded-xl bg-neutral-100 items-center justify-center">
                  <Icon className="w-5 h-5 text-[#111111]" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 font-semibold">
                    Sección {n}
                  </p>
                  <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#111111] leading-tight">
                    {title}
                  </h2>
                </div>
              </div>
              <div className="max-w-none text-neutral-700 leading-relaxed [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-3 [&_ul]:space-y-1 [&_li]:my-1 [&_strong]:text-[#111111] [&_strong]:font-semibold [&_h4]:font-semibold [&_h4]:text-base [&_h4]:mt-5 [&_h4]:mb-2 [&_h4]:text-[#111111]">
                {body}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 p-5 rounded-2xl bg-neutral-100 border border-neutral-200 text-sm text-neutral-600">
          Consulta también nuestros{" "}
          <Link to="/terminos" className="text-terracotta font-semibold hover:underline">
            Términos y Condiciones
          </Link>{" "}
          y la{" "}
          <Link to="/como-funciona" className="text-terracotta font-semibold hover:underline">
            guía Cómo funciona
          </Link>
          .
        </div>
      </section>
    </div>
  );
}
